import { useMemo, useState } from 'react';

import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import legacyTheme from '../../../theme';
import { tokens } from '../../../theme-next';
import type { TaxData } from '../../../types/cg';
import { EntityKindSwitches } from './EntityKindSwitches';
import { FieldEditList } from '../../CGReview/FieldEditList';
import { findFieldLengthViolations } from '../../CGReview/fieldLengthLimits';
import { InlineFileViewer } from '../../CGReview/InlineFileViewer';
import { useFormCGIAStore } from '../store/useFormCGIAStore';
import { reconcileAcquereurExtractedFields } from './acquereurReconcile';
import { DEPARTMENT_FIELD_KEY, PLATE_FIELD_KEY, buildVerifyGroups } from './buildVerifyGroups';
import { detectPlateDeptChange } from './plateDept';
import { SimpleUploadZone } from './SimpleUploadZone';

interface VerifyInfoScreenProps {
  /** Ouvre la certification (parent) — chemin sans modification plaque/département. */
  onRequestFinalSubmission: () => void;
  /** Notification persistante (snackbar parent) — survit au changement d'étape. */
  onNotify: (message: string) => void;
}

/**
 * Étape de vérification (3.5) intercalée entre le dépôt des pièces et la
 * génération de facture.
 *
 * Reprend le cœur de l'écran de review GOD (`InlineFileViewer` +
 * `FieldEditList`) mais SANS les blocs du haut, SANS le score de confiance et
 * SANS les coches de validation : on ne garde que la liaison champ↔pièce
 * (pastilles cliquables qui font pivoter le viewer).
 *
 * Les champs sont éditables. À la soumission :
 *  - si la plaque ou le département ont changé vs l'étape 1 → recalcul ECG Auto
 *    puis retour à l'écran de confirmation du tarif (validation du nouveau prix
 *    avant de finaliser) ;
 *  - sinon → certification puis génération de facture.
 */
export const VerifyInfoScreen = ({ onRequestFinalSubmission, onNotify }: VerifyInfoScreenProps) => {
  const extractedFields = useFormCGIAStore((s) => s.extractedFields);
  const uploadedDocs = useFormCGIAStore((s) => s.uploadedDocs);
  const regPlate = useFormCGIAStore((s) => s.regPlate);
  const department = useFormCGIAStore((s) => s.department);
  const setV2Step = useFormCGIAStore((s) => s.setV2Step);
  const setPlateAndDepartment = useFormCGIAStore((s) => s.setPlateAndDepartment);
  const setTaxData = useFormCGIAStore((s) => s.setTaxData);
  const setTaxConfirmReturn = useFormCGIAStore((s) => s.setTaxConfirmReturn);
  const updateExtractedFieldValue = useFormCGIAStore((s) => s.updateExtractedFieldValue);
  const agencyComment = useFormCGIAStore((s) => s.agencyComment);
  const setAgencyComment = useFormCGIAStore((s) => s.setAgencyComment);
  const agencyCommentFiles = useFormCGIAStore((s) => s.agencyCommentFiles);
  const setAgencyCommentFiles = useFormCGIAStore((s) => s.setAgencyCommentFiles);

  // Départage titulaire / co-titulaire (2 CNI) : l'identité de l'acheteur
  // affichée provient de la CNI réellement rattachée au titulaire (croisement
  // cession + mandat), pas du slot piece_identite brut (cas DY-565-KJ : BEAUSSE
  // titulaire, pas CARCENAC). Les 2 checkboxes co-titulaire sont désormais sur
  // l'écran d'upload (bloc Documents), plus ici.
  const reconciledFields = useMemo(
    () => reconcileAcquereurExtractedFields(extractedFields, uploadedDocs),
    [extractedFields, uploadedDocs],
  );

  const entityKindOverrides = useFormCGIAStore((s) => s.entityKindOverrides);
  const setEntityKindOverride = useFormCGIAStore((s) => s.setEntityKindOverride);

  const { groups, viewerFiles, vendeurNature, acheteurNature } = useMemo(
    () =>
      buildVerifyGroups({
        extractedFields: reconciledFields,
        uploadedDocs,
        entityKindOverrides,
        regPlate,
        department,
      }),
    [reconciledFields, uploadedDocs, entityKindOverrides, regPlate, department],
  );

  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [viewerIndex, setViewerIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Erreurs de champs obligatoires bloquant la soumission (ex : date de vente
  // vide → PurchaseDateTime NOT NULL). Affichées inline sous le champ + toast.
  const [invalidFields, setInvalidFields] = useState<Record<string, string>>({});

  // Valeur plaque actuellement affichée (champ OCR immatriculation, repli sur
  // la plaque saisie à l'étape 1 si la carte grise n'a pas été lue).
  const currentPlateValue =
    extractedFields[PLATE_FIELD_KEY]?.field.value != null
      ? String(extractedFields[PLATE_FIELD_KEY]?.field.value)
      : regPlate;

  const handleSubmit = async () => {
    setError(null);

    // ── Garde champs obligatoires (colonnes NOT NULL) ───────────────────────
    // La date de vente alimente PurchaseDateTime (NOT NULL en base) : sans elle
    // la création du dossier échoue. On bloque AVANT toute suite, avec un toast
    // clair + une erreur inline sur le champ, et on renvoie l'agence le saisir.
    const effectiveDateVente = (
      overrides.date_vente ??
      (extractedFields.date_vente?.field.value != null ? String(extractedFields.date_vente.field.value) : '')
    ).trim();
    if (!effectiveDateVente) {
      setInvalidFields({ date_vente: 'Date de vente obligatoire — renseignez-la pour pouvoir créer le dossier.' });
      onNotify('Il manque la date de vente. Renseignez-la dans la fiche « Véhicule » pour soumettre le dossier.');
      return;
    }

    // ── Garde longueur des champs (bornes des colonnes CG) ──────────────────
    // Une valeur trop longue n'échouait qu'à l'INSERT, en toute fin de
    // soumission, sur une erreur MySQL brute affichée à l'agence : dossier
    // FW-262-LA (12/08/2026), « Chez Madame CHATEIGNIER » rangé par l'OCR dans
    // l'extension du numéro de voie (varchar(10)). On le dit ICI, sur le champ,
    // pendant que l'agence a le dossier sous les yeux.
    const effectiveFieldValues: Record<string, string> = {};
    groups.forEach((group) =>
      group.fields.forEach((f) => {
        effectiveFieldValues[f.fieldKey] = overrides[f.fieldKey] ?? f.value;
      }),
    );
    const lengthViolations = findFieldLengthViolations(effectiveFieldValues);
    const firstViolation = Object.values(lengthViolations)[0];
    if (firstViolation) {
      setInvalidFields(lengthViolations);
      onNotify(`Une information est trop longue pour être enregistrée — ${firstViolation}`);
      return;
    }

    setInvalidFields({});

    const effectivePlate = overrides[PLATE_FIELD_KEY] ?? currentPlateValue;
    const effectiveDept = overrides[DEPARTMENT_FIELD_KEY] ?? department;
    const change = detectPlateDeptChange(regPlate, department, effectivePlate, effectiveDept);

    // 1) Commit des champs OCR édités (hors plaque/département traités à part).
    //    Sans impact tarifaire → sûr quel que soit le chemin.
    Object.entries(overrides).forEach(([key, value]) => {
      if (key === DEPARTMENT_FIELD_KEY || key === PLATE_FIELD_KEY) return;
      updateExtractedFieldValue(key, value);
    });

    if (!change.changed) {
      // 2a) Aucun impact tarifaire → on fige plaque/département normalisés puis
      //     certification directe.
      setPlateAndDepartment(change.normalizedPlate, change.normalizedDept);
      updateExtractedFieldValue(PLATE_FIELD_KEY, change.normalizedPlate);
      onRequestFinalSubmission();
      return;
    }

    // 2b) Plaque/département modifiés → recalcul ECG Auto obligatoire AVANT de
    //     figer la nouvelle référence. Si l'appel échoue, on NE commit PAS
    //     plaque/département : la baseline reste l'ancienne valeur et un nouvel
    //     essai relancera bien le recalcul — sinon on facturerait l'ancien
    //     tarif sur la nouvelle plaque.
    setLoading(true);
    try {
      const resp = await makeAuthenticatedApiCall<TaxData>('post', '/api/misiv/ecg', {
        regPlate: change.normalizedPlate,
        department: change.normalizedDept,
      });
      if (!resp.data || typeof resp.data.taxeTotale !== 'number') {
        throw new Error('Réponse ECG invalide');
      }
      setPlateAndDepartment(change.normalizedPlate, change.normalizedDept);
      updateExtractedFieldValue(PLATE_FIELD_KEY, change.normalizedPlate);
      setTaxData(resp.data as unknown as TaxData);
      setTaxConfirmReturn('final');
      onNotify(
        change.plateChanged && change.deptChanged
          ? 'Plaque et département modifiés — tarif recalculé, validez-le pour finaliser.'
          : change.plateChanged
            ? 'Plaque modifiée — tarif recalculé, validez-le pour finaliser.'
            : 'Département modifié — tarif recalculé, validez-le pour finaliser.',
      );
      setV2Step('tax_confirm');
    } catch (err: any) {
      const data = err?.response?.data;
      const message =
        (typeof data === 'string' && data) ||
        data?.message ||
        data?.error ||
        err?.message ||
        'Impossible de recalculer la taxe pour cette plaque / ce département.';
      setError(String(message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* ── Header minimal (pas de bloc récap / KPI / parcours) ── */}
      <Box>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: tokens.color.yellow,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}
        >
          Vérification du dossier
        </Typography>
        <Typography
          sx={{
            fontSize: '30px',
            fontWeight: 900,
            color: tokens.color.dark,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            marginTop: '6px',
          }}
        >
          Vérifiez les informations avant l'envoi
        </Typography>
        <Typography
          sx={{
            fontSize: '14.5px',
            fontWeight: 500,
            color: tokens.color.textSecondary,
            marginTop: '8px',
            maxWidth: 760,
            lineHeight: 1.55,
          }}
        >
          Cliquez sur une pastille de pièce sous un champ pour afficher le document source. Corrigez ce qui doit l'être
          : toute modification de la plaque ou du département recalculera le tarif.
        </Typography>
      </Box>

      {/* ── Layout 2 colonnes : viewer ↔ champs liés aux pièces ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.1fr) minmax(0, 1fr)' },
          gap: '20px',
          alignItems: 'start',
        }}
      >
        {viewerFiles.length > 0 ? (
          <InlineFileViewer files={viewerFiles} currentIndex={viewerIndex} onChange={setViewerIndex} />
        ) : (
          <Box
            sx={{
              border: `1px dashed ${tokens.color.border}`,
              borderRadius: `${tokens.radius['2xl']}px`,
              padding: '40px 20px',
              textAlign: 'center',
              color: tokens.color.textMuted,
              fontSize: '14px',
            }}
          >
            Aucune pièce à afficher.
          </Box>
        )}

        <Box>
          {/* Bascule particulier / société. La lecture des pièces se trompe
              parfois — un Kbis déposé par erreur, une case mal cochée sur la
              cession — et l'agence n'avait jusqu'ici aucun moyen de la
              contredire (retour François 2026-09-18). Le choix décide aussi de
              ce qui part en base, pas seulement des champs affichés. */}
          <EntityKindSwitches
            vendeurNature={vendeurNature}
            acheteurNature={acheteurNature}
            overrides={entityKindOverrides}
            onChange={setEntityKindOverride}
          />

          <FieldEditList
            groups={groups}
            overrides={overrides}
            onOverrideChange={(fieldKey, value) => {
              setOverrides((prev) => ({ ...prev, [fieldKey]: value }));
              // L'agence corrige un champ signalé en erreur → on lève l'alerte
              // inline immédiatement (feedback premium, pas d'erreur figée).
              setInvalidFields((prev) => {
                if (!(fieldKey in prev)) return prev;
                const next = { ...prev };
                delete next[fieldKey];
                return next;
              });
            }}
            onJumpToDoc={setViewerIndex}
            showConfidence={false}
            invalidFields={invalidFields}
          />
        </Box>
      </Box>

      {/* ── Commentaire pour Skipcar + pièces jointes libres ──── */}
      <Box
        sx={{
          backgroundColor: tokens.color.white,
          border: `0.5px solid ${tokens.color.border}`,
          borderRadius: `${tokens.radius.xl}px`,
          boxShadow: tokens.shadow.card,
          padding: '16px 18px',
        }}
      >
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: tokens.color.textMuted,
            marginBottom: '10px',
          }}
        >
          Commentaire pour Skipcar (facultatif)
        </Typography>
        <TextField
          value={agencyComment}
          onChange={(e) => setAgencyComment(e.target.value)}
          placeholder="Une précision à transmettre à Skipcar sur ce dossier ? (cotitulaire ajouté à la main, pièce particulière…)"
          multiline
          rows={4}
          fullWidth
          size="small"
        />
        <Box sx={{ marginTop: '12px' }}>
          <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: tokens.color.dark, marginBottom: '8px' }}>
            Pièces jointes complémentaires (facultatif)
          </Typography>
          <SimpleUploadZone
            urls={agencyCommentFiles}
            onChange={setAgencyCommentFiles}
            pathPrefix="images"
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ fontSize: '14px' }}>
          {error}
        </Alert>
      )}

      {/* ── Action bar ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          paddingTop: '4px',
        }}
      >
        <ThemeProvider theme={legacyTheme}>
          <Button className="secondaryDealActionButton" onClick={() => setV2Step('ingesting')} disabled={loading}>
            ← Retour aux pièces
          </Button>
          <Button
            className="dealActionButton"
            onClick={() => void handleSubmit()}
            disabled={loading}
            color="primary"
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} thickness={5} color="inherit" /> : undefined}
            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            {loading ? 'Recalcul du tarif…' : 'Soumettre la demande →'}
          </Button>
        </ThemeProvider>
      </Box>
    </Box>
  );
};

export default VerifyInfoScreen;
